import { AuthContext } from '@core/shared';
import { AccountProfile, OptionsApi } from '@core/swagger-client';
import { AccountVerificationRequired, Input, LoaderPage } from '@core/ui';
import { useMutation } from '@tanstack/react-query';
import { useContext, useEffect, useId } from 'react';
import { CountryDropdown } from 'react-country-region-selector';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useValidations, useSubmitValidation } from '../../../hooks';
import { EditProfileFormCancelButton, EditProfileFormSaveButton, EditProfileFormTitle } from '../../atoms';
import { EditBannerImage, EditProfileDataFields, EditProfileFormFooter, EditProfileImage, ImagesInfo } from '../../molecules';

export const EditProfileForm = () => {
    const { t } = useTranslation();
    const {
        register,
        formState: { errors },
        getValues,
        handleSubmit,
        trigger,
        reset,
        setValue,
        watch
    } = useForm<AccountProfile>({ mode: 'onSubmit', reValidateMode: 'onSubmit', defaultValues: {} });
    const validate = useSubmitValidation({ watch, trigger });
    const onSubmit: SubmitHandler<AccountProfile> = (data) => {};
    const countryCode = watch('countryCode');
    const countryId = useId();

    const { companyNameValidation, firstNameValidation, lastNameValidation, nicknameValidation } = useValidations();

    const { isVerified, mainAccount: account, refreshToken } = useContext(AuthContext);

    const updateProfileMutation = useMutation(OptionsApi.account.updateProfile());
    const updateProfile = () => {
        validate(['firstName', 'lastName', 'nickname', 'companyName']).then((isValid) => {
            if (!isValid) return;

            updateProfileMutation.mutate(getValues());
        });
    };

    useEffect(() => {
        // Refresh the account profile
        refreshToken();
    }, []);

    useEffect(() => {
        if (account) {
            reset(account.profile);
        }
    }, [account]);

    if (!account) {
        return <LoaderPage />;
    }

    const profile = account.profile;
    return (
        <div className="relative bg-white rounded-b-lg p-4 md:p-8 z-2">
            {!isVerified && <div className="max-w-136 mb-4 md:mb-6">{!isVerified && <AccountVerificationRequired />}</div>}

            <div className="mb-8 md:mb-12">
                <EditProfileFormTitle profileTitle={t('Account.Profile.PageHeading')} />
                <EditProfileImage account={account} />
                <EditBannerImage account={account} />
                <ImagesInfo />
            </div>

            <EditProfileDataFields onSubmit={handleSubmit(onSubmit)} className="max-w-lg">
                <Input
                    autoFocus
                    label={t('Account.Label.FirstName')}
                    {...register('firstName', firstNameValidation)}
                    errorMessage={errors.firstName?.message}
                    autoComplete="given-name"
                ></Input>

                <Input
                    label={t('Account.Label.LastName')}
                    {...register('lastName', lastNameValidation)}
                    errorMessage={errors.lastName?.message}
                    autoComplete="family-name"
                ></Input>

                <Input
                    label={t('Account.Label.Nickname')}
                    {...register('nickname', nicknameValidation)}
                    errorMessage={errors.nickname?.message}
                    autoComplete="nickname"
                ></Input>

                <Input
                    label={t('Account.Label.CompanyName')}
                    {...register('companyName', companyNameValidation)}
                    errorMessage={errors.companyName?.message}
                    autoComplete="organization"
                ></Input>

                <div className="group relative w-full clear-both mb-10">
                    <label htmlFor={countryId} className="block text-sm">
                        {t('Account.Label.Country')}
                    </label>
                    <div className="flex justify-between">
                        <CountryDropdown
                            id={countryId}
                            className="w-full bg-transparent outline-hidden py-input-vert text-base"
                            value={countryCode ?? ''}
                            onChange={(val) => setValue('countryCode', val)}
                            valueType="short"
                            autoComplete="country"
                        />
                    </div>
                    <div className={`bg-black group-hover:bg-primary group-focus-within:bg-primary input-border`} />
                </div>

                <div className="mb-4 md:mb-10 text-sm">
                    <div>{t('Account.Label.Email')}</div>
                    {profile.emailAddress}
                </div>
            </EditProfileDataFields>

            <EditProfileFormFooter>
                <EditProfileFormCancelButton onClick={() => reset(profile)}>{t('Generic.Button.Cancel')}</EditProfileFormCancelButton>
                <EditProfileFormSaveButton disabled={updateProfileMutation.isPending} onClick={updateProfile}>
                    {t('Generic.Button.SaveChanges')}
                </EditProfileFormSaveButton>
            </EditProfileFormFooter>
            {updateProfileMutation.isSuccess && <p>{t('Account.Profile.Changed.Message')}</p>}
        </div>
    );
};
